import React, { FC, memo, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { css, useTheme } from 'styled-components';
import { ExternalLink, FlexBox, Skeleton, useBreakpoint, useIsMobile } from '@brg/ui';
import { TFile, Project, Competition, Card } from "@brg/api";
import { COVER_VIEWTYPES_1_9, ProjectCardContainerNew, ProjectCardWrapperDoubleNew, ProjectCardWrapperNew } from './stylesNew';
import { CreateLinkTo } from '../../constants/linkTo';
import './ProjectCardLayoutNew.css';
import { AppTester } from "../../../supporting";
import useIsDoubleGrid from "../../hooks/useIsDoubleGrid";
import ResizedImageEnhanced from "../ResizedImageEnhanced";
import { TeamHolder } from '../TeamCard/TeamHolder';
import { ComputedTeam } from 'shared/helpers/types/computed/ComputedTeam';
import { LayoutConst } from 'core/modules/layout.module';
import { ProjectDesignerWrapper } from "../projectDesignerName/styles";
import { SP_VOTE_COLLECTION_SIGN } from "../../pages/VotesAdmin/VotesAdminPage";
import { VoteCollectionCover } from "../../pages/projectEdit/components/VoteCollectionCover/VoteCollectionCover";
import { ModerationType } from "../../pages/teams/const/ModerationTypes";
import { LMSModuleCover } from "../LMSModuleCover/LMSModuleCover";
import { WarholCardCover } from "../WarholCardCover/WarholCardCover";
import { ReactComponent as AgeRestrictedIcon } from '../../assets/ageRestricted.svg';
import { RatingImgGenerate } from 'shared/pages/opencallsPage/GetCompetitionItem/components/RatingImgGenerate';
import { ZharKnigaBadge } from 'shared/pages/opencallsPage/GetCompetitionItem/components/ZharKnigaBadge';
import { useProfile } from '@brg/auth';
import { DesignerCertCardImage } from 'shared/pages/designer/components/tabs/designerDiscountstab';
import { useTranslation } from "react-i18next";
import { ActionsTools } from './components/ActionsTools';
import { OverflowContainer } from './styles';
import { ChannelDefaultEmblem } from 'shared/pages/exploreHseContextsNew/components/ChannelDefaultEmblem';
import { link } from 'fs';
import { BookIconRedMore, BookIconRedMoreDot } from '_deziiign/books/core/components/bookCard/style';
import { useZharKnigaWinner } from 'shared/hooks/useZharKnigaWinner';


const TitleMobile = styled.div<{
  isExpanded?: boolean;
  isClampable?: boolean;
}>`
  margin-top: 16px;

  @media (max-width: ${LayoutConst.screen.md + 'px'}) {
    margin-top: 8px;
  }

  div {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: ${({ isExpanded, isClampable }) =>
    isClampable ? (isExpanded ? 'unset' : 3) : 'unset'};
    overflow: ${({ isExpanded, isClampable }) =>
    isClampable ? (isExpanded ? 'visible' : 'hidden') : 'visible'};
    text-overflow: ellipsis;
    font-family: Inter, sans-serif;
    font-style: normal;
    font-weight: 600;
    font-size: 15px;
    line-height: 20px;
    color: ${({ theme }) => theme.projectView.titleColor};
    transition: all 0.3s ease-in-out;
  }
`;


const SubTitleMobile = styled.div`

  div {
    color: ${({ theme }) => theme.projectView.titleColor};
  }
`;

const AuthorMobile = styled.div<{ isDoubleGrid?: boolean, hasSubtitle?: boolean }>`
  margin-top: 8px;
  ${ProjectDesignerWrapper} {
    color: #909090;
  }
  :hover {
    ${ProjectDesignerWrapper} {
      color: ${(p) => p.theme.multipleAuthors.colorHover} !important;
      svg path {
          fill: ${(p) => p.theme.projectView.authorColorHover};
      }
    }
  }

  @media (max-width: ${LayoutConst.screen.md + 'px'}) {
    margin-top: ${({ hasSubtitle }) => hasSubtitle ? 8 : 4}px;
    span {
      color: ${({ theme }) => theme.projectView.madeBy};
    }
  }
  
  font-family: Inter, sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 17px;
  color: ${({ theme }) => theme.projectView.madeBy};
  
 
`;

export const CoverImage = styled(ResizedImageEnhanced) <{ absolute?: boolean, isSquare?: boolean, isCGRAVE?: boolean, isTeam?: boolean, isLargeProject?: boolean }>`
  position: ${({ absolute }) => absolute ? 'absolute' : 'static'};
  flex-shrink: 0;
  top: 0;
  left: 0;
  height: ${({ isSquare }) => isSquare ? 'initial' : '100%'};
  background: ${({ isCGRAVE }) => isCGRAVE ? '#464645' : ''};
  ${({ isTeam, isLargeProject }) => (isTeam || isLargeProject) && css`
    @media (max-width: ${LayoutConst.screen.s + 'px'}) {
        img{
          object-fit: cover;
        }
      }
  `}
  
`

const MobileMoreWrapper = styled(FlexBox)<{backgroundStyles?: any}>`
  position: absolute;
  top: 0;
  width: 100%;
  // height: 100%;
  padding-right: 8px;

  ${({ backgroundStyles }) => backgroundStyles && css`
    ${backgroundStyles}
    height: 100%;
  `};
`

export interface ProjectCardLayoutNewProps {
  isLoading?: boolean;
  grants?: ReactNode;
  title?: ReactNode;
  subtitle?: ReactNode;
  isOneColumn?: boolean;
  coverTitle?: ReactNode;
  author?: ReactNode;
  likes?: ReactNode;
  point?: ReactNode;
  status?: ReactNode;
  backgroundText?: ReactNode;
  img?: string;
  titleImages?: TFile[];
  identifier?: string;
  project?: Project & any;
  team?: ComputedTeam;
  onClick?: (projectId?: string, e?: any) => void;
  isMoreVisible?: boolean;
  isRejected?: boolean;
  more?: ReactNode;
  href?: string;
  width?: string;
  height?: string;
  isModal?: boolean;
  statusText?: ReactNode;
  rank?: number;
  metaAlwaysVisible?: boolean;
  tooltipOpen?: boolean;
  needLmsCover?: boolean;
  toggleTooltip?: Function;
  linkPrefix?: string;
  isSelectRightNeed?: boolean;
  toggleViewPoint?: (flag: boolean) => void;
  sizeMult?: number;
  withoutGoToProject?: boolean;
  isMobileMoreVisible?: boolean;
  isRemovableFromBook?: boolean;
  deleteButton?: ReactNode;
  bookmarkButton?: ReactNode;
  repostButton?: ReactNode;
  bookmarkHovered?: boolean;
  isTeam?: boolean;
  isBookChapter?: boolean;
  isBook?: boolean;
  hasBottomText?: boolean;
  projectType?: string;
  lmsCardType?: string;
  itemType?: string;
  linkTo?: any;
  linkParams?: string;
  isCert?: boolean;
  card?: Card;
  backgroundStyles?: any;
}


const AgeRestrictedOverlayRoot = styled(FlexBox)`
  position: absolute;
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, rgba(222, 222, 222, 0.60) 0%, rgba(92, 92, 92, 0.60) 100%);
  backdrop-filter: blur(10px);
  z-index: 1;
  align-items: center;
  justify-content: center;
`

export const AgeRestrictedOverlay = () => {
  return <AgeRestrictedOverlayRoot>
    <AgeRestrictedIcon />
  </AgeRestrictedOverlayRoot>
}

const Link = styled(ExternalLink) <{ span: number }>`
  display: initial;
  grid-column: span ${({ span }) => span};
  width: 100%;
`


const ProjectCardLayoutNew: FC<ProjectCardLayoutNewProps> = ({
  isLoading,
  isMoreVisible,
  isRejected,
  isOneColumn,
  grants,
  title,
  subtitle,
  author,
  likes,
  more,
  status,
  width,
  height,
  team,
  statusText,
  metaAlwaysVisible,
  isMobileMoreVisible,
  onClick,
  isBook,
  isModal,
  identifier,
  point,
  rank,
  titleImages,
  isSelectRightNeed,
  sizeMult = 1,
  tooltipOpen = false,
  toggleViewPoint,
  toggleTooltip,
  linkPrefix,
  linkTo,
  withoutGoToProject,
  isRemovableFromBook,
  deleteButton,
  bookmarkButton,
  repostButton,
  isTeam,
  isBookChapter,
  backgroundText,
  backgroundStyles,
  projectType: viewType,
  lmsCardType,
  project,
  itemType,
  linkParams = '',
  coverTitle,
  needLmsCover,
  hasBottomText,
  isCert,
  card
}) => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const isAgeytomesh = AppTester.isAgeytomesh();
  const isDvaDesign = AppTester.isDvaDesign();
  const isSuperPuper = AppTester.isSuperPuper();
  const isCompetition = AppTester.isCompetition();
  const isMedia = AppTester.isMediiia();
  const isSuperPuperMain = useMemo(() => isSuperPuper && location.pathname === '/', [isSuperPuper, location.pathname])
  const isSuperPuperProject = useMemo(() => project?.contexts?.includes('super-puper-collection'), [project])
  const profile = useProfile();
  const isDoubleGrid = useIsDoubleGrid();
  const isSquare = false;
  const is743px = useBreakpoint(743);
  const theme = useTheme();
  const { i18n: { language } } = useTranslation();
  const isHSEDesign = AppTester.isHSEDesign();

  const teamWithoutImage = isTeam && team && (!titleImages || titleImages?.length === 0);

  const hasRootTeam = !!project?.rootTeamId;
  const rootTeamRoute = project?.rootTeam?.route;

  const [isExpanded, setIsExpanded] = useState(false);
  const searchParams = new URLSearchParams(location.search);
  const isProgramsTab = searchParams.get('tab') === 'programs'
  const toggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Добавьте эту строку
    setIsExpanded(!isExpanded);
  };

  const is1_9 = COVER_VIEWTYPES_1_9.includes(String(viewType));
  const isCollectionVote = useMemo(() => {
    return (!titleImages || !Boolean(titleImages?.length)) && (project?.projectContexts?.includes(SP_VOTE_COLLECTION_SIGN) || project?.contexts?.includes(SP_VOTE_COLLECTION_SIGN))
  }, [project, titleImages])


  const isWrongContext = useMemo(() => {
    return !!project && (
      (isDvaDesign && !(project?.contexts?.includes('dvadesign') || project?.projectContexts?.includes('dvadesign'))));
  }, [project])

  const onLinkClick = useCallback((e: any) => {
    if (hasRootTeam) {
      window.open(rootTeamRoute, '_self');
    } else if (isWrongContext) {
      window.open(process.env.REACT_APP_URL_DEZIIIGN + '/project/' + identifier);
    } else {
      onClick?.(identifier ?? '', e);
    }
  }, [onClick, identifier, isWrongContext])

  const isLMS = useMemo(() => team?.moderationType === ModerationType.LMS, [team?.moderationType])

  const isChannel = useMemo(() => team?.moderationType === ModerationType.Channel, [team?.moderationType])

  // const isUnitCourse = useMemo(() => {
  //   //@ts-ignore
  //   return team?.contexts?.includes(contextName)
  // } , [contextName, team])

  const ratingCompetition = project?.competitions?.find((item: Competition) => item.contexts?.includes("ratings"))
  // @ts-ignore
  const isRatingWinner = ratingCompetition?.nominations?.[0]?.winnerPositions?.some(item => item?.winnerId === project?.projectId) ?? false;

  // Жар-книга competition - проверяем через хук, является ли проект победителем
  const { isWinner: isZharKnigaWinner } = useZharKnigaWinner(project?.projectId);

  const isLMSProject = useMemo(() => project?.contexts?.includes('lmshseproject') || project?.contexts?.includes('lmsproject'), [project])

  const needLMSTeamCover = useMemo(
    () => ((needLmsCover || isLMS) && teamWithoutImage)
    , [isLMS, teamWithoutImage, needLmsCover])

  const LMSDiscipline = useMemo(() => project?.additionalInfo?.lmsmodule_discipline || '', [project])
  const [onhoverPlate, setOnhoverPlate] = useState(true);

  const onContainerMouseLeave = (ev: React.SyntheticEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    toggleTooltip && toggleTooltip(false);
    toggleViewPoint && toggleViewPoint(false);
    setOnhoverPlate(true);
  }

  const onContainerMouseEnter = () => {
    toggleViewPoint && toggleViewPoint(true);
    setOnhoverPlate(false);
  }

  const calcGridColumn = useMemo(
    () => (['t-shape', 'fixed-ratio-small', 'fixed-ratio-large', 'horizontal', 'animated'].includes(String(isTeam ? lmsCardType : viewType)) && !isOneColumn ? 2 : 1), [isOneColumn, lmsCardType, isTeam])

  const isLargeProject = calcGridColumn === 2;


  const isBookMarkHidden = useMemo(() => isSuperPuperProject || isRemovableFromBook, [isSuperPuperProject, isRemovableFromBook])

  const isRepostHidden = useMemo(() => {
    return !profile || isTeam || isLMS || isLMSProject || !!team || !!project?.teams || !!project?.rootTeam
  }, [isTeam, isLMS, isLMSProject, profile, team, project])

  const cardBgColor = useMemo(() => {
    return undefined;
  }, [])

  const actionsVisible = useMemo(() => {
    return {
      loading: isLoading,
      showRemove: !(isLoading || !isRemovableFromBook),
      showMore: !(isLoading || !isMoreVisible || isSelectRightNeed),
      showRepost: !(isLoading || isRepostHidden),
      showBookmark: !(isLoading || isBookMarkHidden),
      showStatus: !(isLoading || !isRejected || !status),
      showStatusText: !(isLoading || !statusText),
      showSelect: false,
      showBannedStatus: project?.status === 3,
    }
  } , [
    isRejected,
    isLoading,
    isMoreVisible,
    statusText,
    isRemovableFromBook,
    isBookMarkHidden,
    isRepostHidden,
    isSelectRightNeed,
    status
  ])

  const onDragStart = (e: React.DragEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDragOver = (e: React.DragEvent<HTMLAnchorElement>) => {
    e.preventDefault();
  };

  const calcTitle = useMemo(() => {
    if (!!project?.additionalContent) {
      return project?.additionalContent?.title?.text
    }
    return project?.title
  }, [project])

  const linkTitle = useMemo(() => {
    if (backgroundText) {
      return undefined
    }
    return !!calcTitle ? `${language === 'ru' ? 'Ссылка на проект' : 'Link to project'} - ${calcTitle}` : undefined
  } , [project, calcTitle, backgroundText, language])

  const linkHref = useMemo(() => {
    if (isModal || isWrongContext || backgroundText) return undefined
    return !!linkTo ? linkTo : isLoading ? undefined : hasRootTeam ? rootTeamRoute : ((linkPrefix ? linkPrefix : "") + (CreateLinkTo.projectNew(project) + linkParams))
  } , [isModal, withoutGoToProject, backgroundText, isWrongContext, linkTo, isLoading, hasRootTeam, rootTeamRoute, linkPrefix, linkParams])

  return (
    <Link
      {...linkHref ? { to: linkHref} : {}}
      onClick={onLinkClick}
      span={calcGridColumn}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      title={linkTitle}
    >
      {!location.search.includes('display=doublegrid') && (
        <ProjectCardContainerNew
          onMouseLeave={onContainerMouseLeave}
          onMouseEnter={onContainerMouseEnter}
          {...(isMobile ? {
            onTouchStart: onContainerMouseLeave
          } : {})}
          tooltipOpen={tooltipOpen} // У активной карточки увеличивается z-index и tooltip начинает норм работать 
          width={width}
          height={height}
          isLoading={isLoading}
          isSquare={isSquare}
          noGrid={!isDoubleGrid}
          sizeMult={sizeMult}
          isAgeytomesh={isAgeytomesh}
          cardBgColor={cardBgColor}
          viewType={viewType}
          itemType={itemType}
          hasBottomText={hasBottomText}
        >
        {isBook && !isMobile && <BookIconRedMore><BookIconRedMoreDot isBlue={isHSEDesign}/><BookIconRedMoreDot isBlue={isHSEDesign}/><BookIconRedMoreDot isBlue={isHSEDesign}/></BookIconRedMore>}
          <OverflowContainer isAdaptiveCard={project?.isAdaptiveCard}>
            {project?.isAgeRestricted && <AgeRestrictedOverlay />}
            {/*@ts-ignore*/}
            {!isLoading && (isCert && !!card ? (
              <DesignerCertCardImage card={card} />
            ) : project?.isAdaptiveCard ? (
              <WarholCardCover project={project} />
            ) : isCollectionVote ? (
              <VoteCollectionCover project={project} />
            ) : (teamWithoutImage && !isLMS) ? (
              <ChannelDefaultEmblem team={team}/>
            ) : (isLMSProject || needLMSTeamCover) ? (
              <LMSModuleCover project={project} title={coverTitle || title} discipline={LMSDiscipline} />
            ) : (
              <CoverImage
                isSquare={isSquare}
                showEmptyPlaceholder={!isBookChapter}
                showDefaultPlaceholder={isBookChapter}
                files={titleImages}
                isTeam={isTeam}
                isLargeProject={isLargeProject}
                alt={project?.title}
              />
            ))}
          {point}
        </OverflowContainer>
        {/* Шильдики конкурсов */}
        {(isRatingWinner || isZharKnigaWinner) && (
          <div style={{ position: 'absolute', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {isRatingWinner && ratingCompetition?.isRating && (
              <RatingImgGenerate competition={ratingCompetition} isGridPlate={true} onhoverPlate={!onhoverPlate} />
            )}
            {isZharKnigaWinner && (
              <ZharKnigaBadge isGridBadge={true} onhoverPlate={!onhoverPlate} />
            )}
          </div>
        )}
          {isMobile && isLoading &&
            <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} width={'100%'} height={'100%'} />
          }
            <ProjectCardWrapperNew
              className={metaAlwaysVisible ? 'meta--visible' : ''}
              width={width}
              height={height}
              onClick={(e) => onClick?.(identifier ?? '', e)}
              isLoading={isLoading}
              viewType={viewType}
              isCompetition={isCompetition}
              backgroundStyles={backgroundStyles}
            >
              {(isLoading || !backgroundText) ? null : backgroundText}
              {(isLoading || !statusText) ? null : statusText}
              {isLoading && <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} width={'100%'} height={'100%'} />}
              <FlexBox className="meta" width="100%" justify={isRejected ? 'space-between' : isSelectRightNeed ? 'flex-end' : 'flex-start'} onMouseEnter={() => toggleTooltip && toggleTooltip(true)}>
                <ActionsTools 
                  repost={repostButton}
                  bookmark={bookmarkButton}
                  more={more}
                  remove={deleteButton}
                  status={status}
                  select={undefined}
                  actionsVisible={actionsVisible}
                />
              </FlexBox>
              <FlexBox className="meta" direction={isTeam && (team as any)?.showQu ? "row" : "column"} directionMobile={isTeam && (team as any)?.showQu ? "row" : "column"} width="100%">
                {!!rank && (
                  <div className="ProjectCardLayoutNew__rank">
                    <svg width="12" height="32" viewBox="0 0 12 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M11.9483 0C11.5548 0.937159 10.963 1.78599 10.2101 2.49298C9.45733 3.19997 8.55986 3.74977 7.57433 4.10771C7.58115 2.15851 9.79886 0.0523276 11.9483 0ZM7.50609 28.597C6.01852 26.8179 4.02599 25.7648 2.5316 26.432C3.75987 28.7802 6.25735 29.6174 7.50609 28.597ZM5.1792 25.1434C4.13517 23.1811 2.40877 21.7814 0.805199 22.1476C1.52169 24.6463 3.75305 25.9087 5.1792 25.1434ZM3.66434 21.3889C3.0502 19.3089 1.61722 17.6082 0 17.6736C0.197888 20.2377 2.10853 21.8664 3.66434 21.3889ZM2.98196 17.4643C2.80455 15.3189 1.76734 13.3828 0.136474 13.1408C-0.211535 15.6983 1.36474 17.6606 2.95467 17.4643H2.98196ZM3.08432 13.4874C3.3368 11.3485 2.71584 9.24236 1.17368 8.69946C0.300244 11.1589 1.43298 13.3566 3.05703 13.4874H3.08432ZM3.98505 9.56286C4.66742 7.50247 4.5173 5.29163 3.11844 4.46093C1.73322 6.77642 2.39513 9.15732 3.95776 9.58903L3.98505 9.56286ZM5.67051 5.88685C6.7896 3.97035 7.15126 1.73335 5.95711 0.654095C4.09423 2.69487 4.22388 5.18043 5.67051 5.88685ZM6.6463 6.42321C8.10658 7.15579 9.9831 6.47554 11.2523 5.11502C10.0718 4.05539 7.78586 4.52633 6.6463 6.43629V6.42321ZM5.02908 9.92916C6.61218 10.3739 8.30446 9.37318 9.22567 7.84259C7.84045 7.01189 5.73192 7.84914 5.02908 9.92916ZM4.17612 13.6182C5.81381 13.7556 7.26726 12.467 7.84728 10.8253C6.31876 10.2693 4.44224 11.4859 4.17612 13.6182ZM4.09423 17.3793C5.7251 17.2092 6.89878 15.6721 7.13079 13.9649C5.50674 13.736 3.91681 15.2404 4.09423 17.3793ZM4.7766 21.1011C6.34606 20.6236 7.18538 18.8903 7.06255 17.1765C5.40439 17.2289 4.15564 19.0211 4.7766 21.1011ZM6.20958 24.6528C7.64939 23.8875 8.09975 22.0364 7.63574 20.3358C6.01852 20.6956 5.15191 22.7036 6.18229 24.6528H6.20958ZM8.4 27.9233C9.64874 26.8964 9.66921 24.9341 8.85719 23.3446C7.34232 24.0184 6.91243 26.1638 8.3727 27.9233H8.4ZM11.8119 31.4489C7.61795 28.4427 4.7854 24.0102 3.8969 19.0632C3.00841 14.1161 4.13154 9.03084 7.03525 4.85338C4.0172 9.04293 2.7919 14.1831 3.61213 19.2133C4.43236 24.2436 7.23556 28.7802 11.4434 31.8871C11.6344 32.031 11.8664 32.0441 11.962 31.8871C12.0575 31.7301 11.962 31.5601 11.7982 31.4227L11.8119 31.4489Z"
                        fill="#F2C94C" />
                    </svg>

                    {rank > 3 ? (
                      <svg className="ProjectCardLayoutNew__rank-heart" width="20" height="18" viewBox="0 0 20 18"
                        fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" clipRule="evenodd"
                          d="M18.2169 1.78212C15.9715 -0.4646 12.3921 -0.58732 10.0005 1.41584C7.60884 -0.58732 4.03036 -0.4646 1.78403 1.78212C0.633032 2.93379 0 4.46401 0 6.09336C0 7.72175 0.633032 9.25292 1.78403 10.4046L8.93342 17.5582C9.22683 17.8527 9.61366 18 10.0005 18C10.3873 18 10.7731 17.8527 11.0675 17.5582L18.2169 10.4046C19.3669 9.25386 20 7.7227 20 6.09336C20 4.46401 19.3669 2.93285 18.2169 1.78212Z"
                          fill="#FEFEFE" />
                      </svg>
                    ) : (
                      <div className="ProjectCardLayoutNew__rank-value">
                        {rank}
                      </div>
                    )}

                    <svg width="12" height="32" viewBox="0 0 12 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M0.0516688 0C0.445203 0.937159 1.03704 1.78599 1.78986 2.49298C2.54267 3.19997 3.44014 3.74977 4.42567 4.10771C4.41885 2.15851 2.20114 0.0523276 0.0516688 0ZM4.49391 28.597C5.98148 26.8179 7.97401 25.7648 9.4684 26.432C8.24013 28.7802 5.74265 29.6174 4.49391 28.597ZM6.8208 25.1434C7.86483 23.1811 9.59123 21.7814 11.1948 22.1476C10.4783 24.6463 8.24695 25.9087 6.8208 25.1434ZM8.33566 21.3889C8.9498 19.3089 10.3828 17.6082 12 17.6736C11.8021 20.2377 9.89147 21.8664 8.33566 21.3889ZM9.01804 17.4643C9.19545 15.3189 10.2327 13.3828 11.8635 13.1408C12.2115 15.6983 10.6353 17.6606 9.04533 17.4643H9.01804ZM8.91568 13.4874C8.6632 11.3485 9.28416 9.24236 10.8263 8.69946C11.6998 11.1589 10.567 13.3566 8.94297 13.4874H8.91568ZM8.01495 9.56286C7.33258 7.50247 7.4827 5.29163 8.88156 4.46093C10.2668 6.77642 9.60487 9.15732 8.04224 9.58903L8.01495 9.56286ZM6.32949 5.88685C5.2104 3.97035 4.84874 1.73335 6.04289 0.654095C7.90577 2.69487 7.77612 5.18043 6.32949 5.88685ZM5.3537 6.42321C3.89342 7.15579 2.0169 6.47554 0.747688 5.11502C1.92819 4.05539 4.21414 4.52633 5.3537 6.43629V6.42321ZM6.97092 9.92916C5.38782 10.3739 3.69553 9.37318 2.77433 7.84259C4.15955 7.01189 6.26808 7.84914 6.97092 9.92916ZM7.82388 13.6182C6.18619 13.7556 4.73274 12.467 4.15272 10.8253C5.68124 10.2693 7.55776 11.4859 7.82388 13.6182ZM7.90577 17.3793C6.2749 17.2092 5.10122 15.6721 4.86921 13.9649C6.49326 13.736 8.08319 15.2404 7.90577 17.3793ZM7.2234 21.1011C5.65394 20.6236 4.81462 18.8903 4.93745 17.1765C6.59561 17.2289 7.84436 19.0211 7.2234 21.1011ZM5.79042 24.6528C4.35061 23.8875 3.90025 22.0364 4.36426 20.3358C5.98148 20.6956 6.84809 22.7036 5.81771 24.6528H5.79042ZM3.6 27.9233C2.35126 26.8964 2.33079 24.9341 3.14281 23.3446C4.65768 24.0184 5.08757 26.1638 3.6273 27.9233H3.6ZM0.188143 31.4489C4.38205 28.4427 7.2146 24.0102 8.1031 19.0632C8.99159 14.1161 7.86846 9.03084 4.96475 4.85338C7.9828 9.04293 9.2081 14.1831 8.38787 19.2133C7.56764 24.2436 4.76444 28.7802 0.556624 31.8871C0.365559 32.031 0.133553 32.0441 0.038021 31.8871C-0.057511 31.7301 0.0380208 31.5601 0.20179 31.4227L0.188143 31.4489Z"
                        fill="#F2C94C" />
                    </svg>
                  </div>
                )}

                {(!!grants) && (
                  <FlexBox width="100%" className="pcw" height={32} offsetBottom={8} gx={20}>
                    {isLoading ? (
                      <>
                      </>
                    ) : grants}
                  </FlexBox>
                )}
                {!isMobile && !isLoading && !isSuperPuperMain && <FlexBox width="100%" offsetBottom={4}>
                  {isLoading || is1_9 ? null : title}
                </FlexBox>}
                {!isMobile && subtitle && !(isLoading || is1_9) && <FlexBox offsetBottom={8}>{subtitle}</FlexBox>}
                  <FlexBox align="flex-end" width="100%" justify="space-between">
                    <div
                      onClick={(event) => event.stopPropagation()}
                      onMouseEnter={() => toggleTooltip && toggleTooltip(true)}
                      style={{ maxWidth: !isSuperPuperMain ? '70%' : '100%'}}
                    >
                      {/*{isLoading ? <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} height={20} width={100}/> : author}*/}
                      {!isLoading && isSuperPuperMain && <FlexBox width="100%">
                        {isLoading || is1_9 ? null : title}
                      </FlexBox>}
                      {isSuperPuperMain || isLoading || (isTeam && !isChannel) || is1_9 || isMobile ? null : author}
                    </div>
                    <div> {isLoading ? null : likes}</div>
                  </FlexBox>
              </FlexBox>
              {point}
            </ProjectCardWrapperNew>
        </ProjectCardContainerNew>
      )}

      {location.search.includes('display=doublegrid') && (
        <ProjectCardContainerNew
          onMouseLeave={() => toggleTooltip && toggleTooltip(false)}
          tooltipOpen={tooltipOpen}
          width={width}
          height={height}
          itemType={itemType}
          sizeMult={sizeMult}
          isAgeytomesh={isAgeytomesh}
          isLoading={true}>
          <OverflowContainer>
            <CoverImage isSquare={isSquare} files={titleImages} />
          </OverflowContainer>
          <ProjectCardWrapperDoubleNew
            className={metaAlwaysVisible ? 'meta--visible' : ''}
            onClick={(e) => onClick?.(identifier ?? '', e)}
            isLoading={isLoading}
          >
            <FlexBox className="meta" width="100%" justify={isRejected ? 'space-between' : 'flex-start'} onMouseEnter={() => toggleTooltip && toggleTooltip(true)}>
              <ActionsTools 
                status={status}
                select={undefined}
                more={more}
                bookmark={bookmarkButton}
                remove={deleteButton}
                repost={repostButton}
                actionsVisible={actionsVisible}
              />
            </FlexBox>
            <FlexBox className="meta" direction="column" directionMobile="column" width="100%">
              <FlexBox width="100%" className="pcw" height={32} offsetBottom={8} gx={20}>
                {isLoading && <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} width={32} height={32} />}
                {isLoading && <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} width={32} height={32} />}
                {!isLoading ? grants : null}
              </FlexBox>
              {!isCollectionVote && <FlexBox width="100%" offsetBottom={4}>
                {isLoading ? <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} height={32} width="100%" /> : title}
              </FlexBox>}
              {subtitle && !isLoading && <FlexBox offsetBottom={8}>{subtitle}</FlexBox>}
              <FlexBox align="flex-end" width="100%" justify="space-between">
                {!isSuperPuperMain && <div
                  onClick={(event) => event.stopPropagation()}
                  onMouseEnter={() => toggleTooltip && toggleTooltip(true)}>
                  {isLoading ? <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} height={20} width={100} /> : author}
                </div>}
                <div> {isLoading ? <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} height={20} width={25} /> : likes}</div>
              </FlexBox>
            </FlexBox>
          </ProjectCardWrapperDoubleNew>
        </ProjectCardContainerNew>
      )}

      {(isMobile || is1_9) && (
        <FlexBox directionMobile="column" offsetTop={0} direction="column">
          {isLoading && <>
            <Skeleton offsetTop={8} style={{ backgroundColor: theme.skeleton.skeletonBgColor }} width={'50%'} height={'23px'} />
            <Skeleton offsetTop={2} style={{ backgroundColor: theme.skeleton.skeletonBgColor }} width={'100%'} height={'14px'} />
          </>}
          {
            <div style={{ position: 'relative' }}>
              {isMobile && (isProgramsTab || isChannel)
                ? (
                  <div style={{ position: 'relative' }}>
                    <TitleMobile isExpanded={isExpanded} onClick={toggle} isClampable={isProgramsTab || isChannel}>
                      <div>{title}</div>
                    </TitleMobile>
                  </div>
                ) : (
                  <TitleMobile>
                    <div>{title}</div>
                  </TitleMobile>
                )}
            </div>
          }
          {subtitle && <FlexBox offsetTop={4}><SubTitleMobile>{subtitle}</SubTitleMobile></FlexBox>}
          {(!isSuperPuperMain && !isTeam) && <AuthorMobile hasSubtitle={Boolean(subtitle)} isDoubleGrid={isDoubleGrid}>{author}</AuthorMobile>}
          {!isMedia && !isDoubleGrid && likes}
        </FlexBox>
      )}
    </Link>
  );
};

export default memo(ProjectCardLayoutNew)
import React, { FC, memo, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { css, useTheme } from 'styled-components';
import { ExternalLink, FlexBox, Skeleton, useBreakpoint, useIsMobile } from '@brg/ui';
import { TFile, Project, Competition, Card } from "@brg/api";
import { COVER_VIEWTYPES_1_9, ProjectCardContainerNew, ProjectCardWrapperDoubleNew, ProjectCardWrapperNew } from './stylesNew';
import { CreateLinkTo } from '../../constants/linkTo';
import './ProjectCardLayoutNew.css';
import { AppTester } from "../../../supporting";
import useIsDoubleGrid from "../../hooks/useIsDoubleGrid";
import ResizedImageEnhanced from "../ResizedImageEnhanced";
import { TeamHolder } from '../TeamCard/TeamHolder';
import { ComputedTeam } from 'shared/helpers/types/computed/ComputedTeam';
import { LayoutConst } from 'core/modules/layout.module';
import { ProjectDesignerWrapper } from "../projectDesignerName/styles";
import { SP_VOTE_COLLECTION_SIGN } from "../../pages/VotesAdmin/VotesAdminPage";
import { VoteCollectionCover } from "../../pages/projectEdit/components/VoteCollectionCover/VoteCollectionCover";
import { ModerationType } from "../../pages/teams/const/ModerationTypes";
import { LMSModuleCover } from "../LMSModuleCover/LMSModuleCover";
import { WarholCardCover } from "../WarholCardCover/WarholCardCover";
import { ReactComponent as AgeRestrictedIcon } from '../../assets/ageRestricted.svg';
import { RatingImgGenerate } from 'shared/pages/opencallsPage/GetCompetitionItem/components/RatingImgGenerate';
import { ZharKnigaBadge } from 'shared/pages/opencallsPage/GetCompetitionItem/components/ZharKnigaBadge';
import { useProfile } from '@brg/auth';
import { DesignerCertCardImage } from 'shared/pages/designer/components/tabs/designerDiscountstab';
import { useTranslation } from "react-i18next";
import { ActionsTools } from './components/ActionsTools';
import { OverflowContainer } from './styles';
import { ChannelDefaultEmblem } from 'shared/pages/exploreHseContextsNew/components/ChannelDefaultEmblem';
import { link } from 'fs';
import { BookIconRedMore, BookIconRedMoreDot } from '_deziiign/books/core/components/bookCard/style';
import { useZharKnigaWinner } from 'shared/hooks/useZharKnigaWinner';


const TitleMobile = styled.div<{
  isExpanded?: boolean;
  isClampable?: boolean;
}>`
  margin-top: 16px;

  @media (max-width: ${LayoutConst.screen.md + 'px'}) {
    margin-top: 8px;
  }

  div {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: ${({ isExpanded, isClampable }) =>
    isClampable ? (isExpanded ? 'unset' : 3) : 'unset'};
    overflow: ${({ isExpanded, isClampable }) =>
    isClampable ? (isExpanded ? 'visible' : 'hidden') : 'visible'};
    text-overflow: ellipsis;
    font-family: Inter, sans-serif;
    font-style: normal;
    font-weight: 600;
    font-size: 15px;
    line-height: 20px;
    color: ${({ theme }) => theme.projectView.titleColor};
    transition: all 0.3s ease-in-out;
  }
`;


const SubTitleMobile = styled.div`

  div {
    color: ${({ theme }) => theme.projectView.titleColor};
  }
`;

const AuthorMobile = styled.div<{ isDoubleGrid?: boolean, hasSubtitle?: boolean }>`
  margin-top: 8px;
  ${ProjectDesignerWrapper} {
    color: #909090;
  }
  :hover {
    ${ProjectDesignerWrapper} {
      color: ${(p) => p.theme.multipleAuthors.colorHover} !important;
      svg path {
          fill: ${(p) => p.theme.projectView.authorColorHover};
      }
    }
  }

  @media (max-width: ${LayoutConst.screen.md + 'px'}) {
    margin-top: ${({ hasSubtitle }) => hasSubtitle ? 8 : 4}px;
    span {
      color: ${({ theme }) => theme.projectView.madeBy};
    }
  }
  
  font-family: Inter, sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 17px;
  color: ${({ theme }) => theme.projectView.madeBy};
  
 
`;

export const CoverImage = styled(ResizedImageEnhanced) <{ absolute?: boolean, isSquare?: boolean, isCGRAVE?: boolean, isTeam?: boolean, isLargeProject?: boolean }>`
  position: ${({ absolute }) => absolute ? 'absolute' : 'static'};
  flex-shrink: 0;
  top: 0;
  left: 0;
  height: ${({ isSquare }) => isSquare ? 'initial' : '100%'};
  background: ${({ isCGRAVE }) => isCGRAVE ? '#464645' : ''};
  ${({ isTeam, isLargeProject }) => (isTeam || isLargeProject) && css`
    @media (max-width: ${LayoutConst.screen.s + 'px'}) {
        img{
          object-fit: cover;
        }
      }
  `}
  
`

const MobileMoreWrapper = styled(FlexBox)<{backgroundStyles?: any}>`
  position: absolute;
  top: 0;
  width: 100%;
  // height: 100%;
  padding-right: 8px;

  ${({ backgroundStyles }) => backgroundStyles && css`
    ${backgroundStyles}
    height: 100%;
  `};
`

export interface ProjectCardLayoutNewProps {
  isLoading?: boolean;
  grants?: ReactNode;
  title?: ReactNode;
  subtitle?: ReactNode;
  isOneColumn?: boolean;
  coverTitle?: ReactNode;
  author?: ReactNode;
  likes?: ReactNode;
  point?: ReactNode;
  status?: ReactNode;
  backgroundText?: ReactNode;
  img?: string;
  titleImages?: TFile[];
  identifier?: string;
  project?: Project & any;
  team?: ComputedTeam;
  onClick?: (projectId?: string, e?: any) => void;
  isMoreVisible?: boolean;
  isRejected?: boolean;
  more?: ReactNode;
  href?: string;
  width?: string;
  height?: string;
  isModal?: boolean;
  statusText?: ReactNode;
  rank?: number;
  metaAlwaysVisible?: boolean;
  tooltipOpen?: boolean;
  needLmsCover?: boolean;
  toggleTooltip?: Function;
  linkPrefix?: string;
  isSelectRightNeed?: boolean;
  toggleViewPoint?: (flag: boolean) => void;
  sizeMult?: number;
  withoutGoToProject?: boolean;
  isMobileMoreVisible?: boolean;
  isRemovableFromBook?: boolean;
  deleteButton?: ReactNode;
  bookmarkButton?: ReactNode;
  repostButton?: ReactNode;
  bookmarkHovered?: boolean;
  isTeam?: boolean;
  isBookChapter?: boolean;
  isBook?: boolean;
  hasBottomText?: boolean;
  projectType?: string;
  lmsCardType?: string;
  itemType?: string;
  linkTo?: any;
  linkParams?: string;
  isCert?: boolean;
  card?: Card;
  backgroundStyles?: any;
}


const AgeRestrictedOverlayRoot = styled(FlexBox)`
  position: absolute;
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, rgba(222, 222, 222, 0.60) 0%, rgba(92, 92, 92, 0.60) 100%);
  backdrop-filter: blur(10px);
  z-index: 1;
  align-items: center;
  justify-content: center;
`

export const AgeRestrictedOverlay = () => {
  return <AgeRestrictedOverlayRoot>
    <AgeRestrictedIcon />
  </AgeRestrictedOverlayRoot>
}

const Link = styled(ExternalLink) <{ span: number }>`
  display: initial;
  grid-column: span ${({ span }) => span};
  width: 100%;
`


const ProjectCardLayoutNew: FC<ProjectCardLayoutNewProps> = ({
  isLoading,
  isMoreVisible,
  isRejected,
  isOneColumn,
  grants,
  title,
  subtitle,
  author,
  likes,
  more,
  status,
  width,
  height,
  team,
  statusText,
  metaAlwaysVisible,
  isMobileMoreVisible,
  onClick,
  isBook,
  isModal,
  identifier,
  point,
  rank,
  titleImages,
  isSelectRightNeed,
  sizeMult = 1,
  tooltipOpen = false,
  toggleViewPoint,
  toggleTooltip,
  linkPrefix,
  linkTo,
  withoutGoToProject,
  isRemovableFromBook,
  deleteButton,
  bookmarkButton,
  repostButton,
  isTeam,
  isBookChapter,
  backgroundText,
  backgroundStyles,
  projectType: viewType,
  lmsCardType,
  project,
  itemType,
  linkParams = '',
  coverTitle,
  needLmsCover,
  hasBottomText,
  isCert,
  card
}) => {
  const location = useLocation();
  const isMobile = useIsMobile();
  const isAgeytomesh = AppTester.isAgeytomesh();
  const isDvaDesign = AppTester.isDvaDesign();
  const isSuperPuper = AppTester.isSuperPuper();
  const isCompetition = AppTester.isCompetition();
  const isMedia = AppTester.isMediiia();
  const isSuperPuperMain = useMemo(() => isSuperPuper && location.pathname === '/', [isSuperPuper, location.pathname])
  const isSuperPuperProject = useMemo(() => project?.contexts?.includes('super-puper-collection'), [project])
  const profile = useProfile();
  const isDoubleGrid = useIsDoubleGrid();
  const isSquare = false;
  const is743px = useBreakpoint(743);
  const theme = useTheme();
  const { i18n: { language } } = useTranslation();
  const isHSEDesign = AppTester.isHSEDesign();

  const teamWithoutImage = isTeam && team && (!titleImages || titleImages?.length === 0);

  const hasRootTeam = !!project?.rootTeamId;
  const rootTeamRoute = project?.rootTeam?.route;

  const [isExpanded, setIsExpanded] = useState(false);
  const searchParams = new URLSearchParams(location.search);
  const isProgramsTab = searchParams.get('tab') === 'programs'
  const toggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation(); // Добавьте эту строку
    setIsExpanded(!isExpanded);
  };

  const is1_9 = COVER_VIEWTYPES_1_9.includes(String(viewType));
  const isCollectionVote = useMemo(() => {
    return (!titleImages || !Boolean(titleImages?.length)) && (project?.projectContexts?.includes(SP_VOTE_COLLECTION_SIGN) || project?.contexts?.includes(SP_VOTE_COLLECTION_SIGN))
  }, [project, titleImages])


  const isWrongContext = useMemo(() => {
    return !!project && (
      (isDvaDesign && !(project?.contexts?.includes('dvadesign') || project?.projectContexts?.includes('dvadesign'))));
  }, [project])

  const onLinkClick = useCallback((e: any) => {
    if (hasRootTeam) {
      window.open(rootTeamRoute, '_self');
    } else if (isWrongContext) {
      window.open(process.env.REACT_APP_URL_DEZIIIGN + '/project/' + identifier);
    } else {
      onClick?.(identifier ?? '', e);
    }
  }, [onClick, identifier, isWrongContext])

  const isLMS = useMemo(() => team?.moderationType === ModerationType.LMS, [team?.moderationType])

  const isChannel = useMemo(() => team?.moderationType === ModerationType.Channel, [team?.moderationType])

  // const isUnitCourse = useMemo(() => {
  //   //@ts-ignore
  //   return team?.contexts?.includes(contextName)
  // } , [contextName, team])

  const ratingCompetition = project?.competitions?.find((item: Competition) => item.contexts?.includes("ratings"))
  // @ts-ignore
  const isRatingWinner = ratingCompetition?.nominations?.[0]?.winnerPositions?.some(item => item?.winnerId === project?.projectId) ?? false;

  // Жар-книга competition - проверяем через хук, является ли проект победителем
  const { isWinner: isZharKnigaWinner } = useZharKnigaWinner(project?.projectId);

  const isLMSProject = useMemo(() => project?.contexts?.includes('lmshseproject') || project?.contexts?.includes('lmsproject'), [project])

  const needLMSTeamCover = useMemo(
    () => ((needLmsCover || isLMS) && teamWithoutImage)
    , [isLMS, teamWithoutImage, needLmsCover])

  const LMSDiscipline = useMemo(() => project?.additionalInfo?.lmsmodule_discipline || '', [project])
  const [onhoverPlate, setOnhoverPlate] = useState(true);

  const onContainerMouseLeave = (ev: React.SyntheticEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    toggleTooltip && toggleTooltip(false);
    toggleViewPoint && toggleViewPoint(false);
    setOnhoverPlate(true);
  }

  const onContainerMouseEnter = () => {
    toggleViewPoint && toggleViewPoint(true);
    setOnhoverPlate(false);
  }

  const calcGridColumn = useMemo(
    () => (['t-shape', 'fixed-ratio-small', 'fixed-ratio-large', 'horizontal', 'animated'].includes(String(isTeam ? lmsCardType : viewType)) && !isOneColumn ? 2 : 1), [isOneColumn, lmsCardType, isTeam])

  const isLargeProject = calcGridColumn === 2;


  const isBookMarkHidden = useMemo(() => isSuperPuperProject || isRemovableFromBook, [isSuperPuperProject, isRemovableFromBook])

  const isRepostHidden = useMemo(() => {
    return !profile || isTeam || isLMS || isLMSProject || !!team || !!project?.teams || !!project?.rootTeam
  }, [isTeam, isLMS, isLMSProject, profile, team, project])

  const cardBgColor = useMemo(() => {
    return undefined;
  }, [])

  const actionsVisible = useMemo(() => {
    return {
      loading: isLoading,
      showRemove: !(isLoading || !isRemovableFromBook),
      showMore: !(isLoading || !isMoreVisible || isSelectRightNeed),
      showRepost: !(isLoading || isRepostHidden),
      showBookmark: !(isLoading || isBookMarkHidden),
      showStatus: !(isLoading || !isRejected || !status),
      showStatusText: !(isLoading || !statusText),
      showSelect: false,
      showBannedStatus: project?.status === 3,
    }
  } , [
    isRejected,
    isLoading,
    isMoreVisible,
    statusText,
    isRemovableFromBook,
    isBookMarkHidden,
    isRepostHidden,
    isSelectRightNeed,
    status
  ])

  const onDragStart = (e: React.DragEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDragOver = (e: React.DragEvent<HTMLAnchorElement>) => {
    e.preventDefault();
  };

  const calcTitle = useMemo(() => {
    if (!!project?.additionalContent) {
      return project?.additionalContent?.title?.text
    }
    return project?.title
  }, [project])

  const linkTitle = useMemo(() => {
    if (backgroundText) {
      return undefined
    }
    return !!calcTitle ? `${language === 'ru' ? 'Ссылка на проект' : 'Link to project'} - ${calcTitle}` : undefined
  } , [project, calcTitle, backgroundText, language])

  const linkHref = useMemo(() => {
    if (isModal || isWrongContext || backgroundText) return undefined
    return !!linkTo ? linkTo : isLoading ? undefined : hasRootTeam ? rootTeamRoute : ((linkPrefix ? linkPrefix : "") + (CreateLinkTo.projectNew(project) + linkParams))
  } , [isModal, withoutGoToProject, backgroundText, isWrongContext, linkTo, isLoading, hasRootTeam, rootTeamRoute, linkPrefix, linkParams])

  return (
    <Link
      {...linkHref ? { to: linkHref} : {}}
      onClick={onLinkClick}
      span={calcGridColumn}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      title={linkTitle}
    >
      {!location.search.includes('display=doublegrid') && (
        <ProjectCardContainerNew
          onMouseLeave={onContainerMouseLeave}
          onMouseEnter={onContainerMouseEnter}
          {...(isMobile ? {
            onTouchStart: onContainerMouseLeave
          } : {})}
          tooltipOpen={tooltipOpen} // У активной карточки увеличивается z-index и tooltip начинает норм работать 
          width={width}
          height={height}
          isLoading={isLoading}
          isSquare={isSquare}
          noGrid={!isDoubleGrid}
          sizeMult={sizeMult}
          isAgeytomesh={isAgeytomesh}
          cardBgColor={cardBgColor}
          viewType={viewType}
          itemType={itemType}
          hasBottomText={hasBottomText}
        >
        {isBook && !isMobile && <BookIconRedMore><BookIconRedMoreDot isBlue={isHSEDesign}/><BookIconRedMoreDot isBlue={isHSEDesign}/><BookIconRedMoreDot isBlue={isHSEDesign}/></BookIconRedMore>}
          <OverflowContainer isAdaptiveCard={project?.isAdaptiveCard}>
            {project?.isAgeRestricted && <AgeRestrictedOverlay />}
            {/*@ts-ignore*/}
            {!isLoading && (isCert && !!card ? (
              <DesignerCertCardImage card={card} />
            ) : project?.isAdaptiveCard ? (
              <WarholCardCover project={project} />
            ) : isCollectionVote ? (
              <VoteCollectionCover project={project} />
            ) : (teamWithoutImage && !isLMS) ? (
              <ChannelDefaultEmblem team={team}/>
            ) : (isLMSProject || needLMSTeamCover) ? (
              <LMSModuleCover project={project} title={coverTitle || title} discipline={LMSDiscipline} />
            ) : (
              <CoverImage
                isSquare={isSquare}
                showEmptyPlaceholder={!isBookChapter}
                showDefaultPlaceholder={isBookChapter}
                files={titleImages}
                isTeam={isTeam}
                isLargeProject={isLargeProject}
                alt={project?.title}
              />
            ))}
          {point}
        </OverflowContainer>
        {/* Шильдики конкурсов */}
        {(isRatingWinner || isZharKnigaWinner) && (
          <div style={{ position: 'absolute', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {isRatingWinner && ratingCompetition?.isRating && (
              <RatingImgGenerate competition={ratingCompetition} isGridPlate={true} onhoverPlate={!onhoverPlate} />
            )}
            {isZharKnigaWinner && (
              <ZharKnigaBadge isGridBadge={true} onhoverPlate={!onhoverPlate} />
            )}
          </div>
        )}
          {isMobile && isLoading &&
            <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} width={'100%'} height={'100%'} />
          }
            <ProjectCardWrapperNew
              className={metaAlwaysVisible ? 'meta--visible' : ''}
              width={width}
              height={height}
              onClick={(e) => onClick?.(identifier ?? '', e)}
              isLoading={isLoading}
              viewType={viewType}
              isCompetition={isCompetition}
              backgroundStyles={backgroundStyles}
            >
              {(isLoading || !backgroundText) ? null : backgroundText}
              {(isLoading || !statusText) ? null : statusText}
              {isLoading && <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} width={'100%'} height={'100%'} />}
              <FlexBox className="meta" width="100%" justify={isRejected ? 'space-between' : isSelectRightNeed ? 'flex-end' : 'flex-start'} onMouseEnter={() => toggleTooltip && toggleTooltip(true)}>
                <ActionsTools 
                  repost={repostButton}
                  bookmark={bookmarkButton}
                  more={more}
                  remove={deleteButton}
                  status={status}
                  select={undefined}
                  actionsVisible={actionsVisible}
                />
              </FlexBox>
              <FlexBox className="meta" direction={isTeam && (team as any)?.showQu ? "row" : "column"} directionMobile={isTeam && (team as any)?.showQu ? "row" : "column"} width="100%">
                {!!rank && (
                  <div className="ProjectCardLayoutNew__rank">
                    <svg width="12" height="32" viewBox="0 0 12 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M11.9483 0C11.5548 0.937159 10.963 1.78599 10.2101 2.49298C9.45733 3.19997 8.55986 3.74977 7.57433 4.10771C7.58115 2.15851 9.79886 0.0523276 11.9483 0ZM7.50609 28.597C6.01852 26.8179 4.02599 25.7648 2.5316 26.432C3.75987 28.7802 6.25735 29.6174 7.50609 28.597ZM5.1792 25.1434C4.13517 23.1811 2.40877 21.7814 0.805199 22.1476C1.52169 24.6463 3.75305 25.9087 5.1792 25.1434ZM3.66434 21.3889C3.0502 19.3089 1.61722 17.6082 0 17.6736C0.197888 20.2377 2.10853 21.8664 3.66434 21.3889ZM2.98196 17.4643C2.80455 15.3189 1.76734 13.3828 0.136474 13.1408C-0.211535 15.6983 1.36474 17.6606 2.95467 17.4643H2.98196ZM3.08432 13.4874C3.3368 11.3485 2.71584 9.24236 1.17368 8.69946C0.300244 11.1589 1.43298 13.3566 3.05703 13.4874H3.08432ZM3.98505 9.56286C4.66742 7.50247 4.5173 5.29163 3.11844 4.46093C1.73322 6.77642 2.39513 9.15732 3.95776 9.58903L3.98505 9.56286ZM5.67051 5.88685C6.7896 3.97035 7.15126 1.73335 5.95711 0.654095C4.09423 2.69487 4.22388 5.18043 5.67051 5.88685ZM6.6463 6.42321C8.10658 7.15579 9.9831 6.47554 11.2523 5.11502C10.0718 4.05539 7.78586 4.52633 6.6463 6.43629V6.42321ZM5.02908 9.92916C6.61218 10.3739 8.30446 9.37318 9.22567 7.84259C7.84045 7.01189 5.73192 7.84914 5.02908 9.92916ZM4.17612 13.6182C5.81381 13.7556 7.26726 12.467 7.84728 10.8253C6.31876 10.2693 4.44224 11.4859 4.17612 13.6182ZM4.09423 17.3793C5.7251 17.2092 6.89878 15.6721 7.13079 13.9649C5.50674 13.736 3.91681 15.2404 4.09423 17.3793ZM4.7766 21.1011C6.34606 20.6236 7.18538 18.8903 7.06255 17.1765C5.40439 17.2289 4.15564 19.0211 4.7766 21.1011ZM6.20958 24.6528C7.64939 23.8875 8.09975 22.0364 7.63574 20.3358C6.01852 20.6956 5.15191 22.7036 6.18229 24.6528H6.20958ZM8.4 27.9233C9.64874 26.8964 9.66921 24.9341 8.85719 23.3446C7.34232 24.0184 6.91243 26.1638 8.3727 27.9233H8.4ZM11.8119 31.4489C7.61795 28.4427 4.7854 24.0102 3.8969 19.0632C3.00841 14.1161 4.13154 9.03084 7.03525 4.85338C4.0172 9.04293 2.7919 14.1831 3.61213 19.2133C4.43236 24.2436 7.23556 28.7802 11.4434 31.8871C11.6344 32.031 11.8664 32.0441 11.962 31.8871C12.0575 31.7301 11.962 31.5601 11.7982 31.4227L11.8119 31.4489Z"
                        fill="#F2C94C" />
                    </svg>

                    {rank > 3 ? (
                      <svg className="ProjectCardLayoutNew__rank-heart" width="20" height="18" viewBox="0 0 20 18"
                        fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" clipRule="evenodd"
                          d="M18.2169 1.78212C15.9715 -0.4646 12.3921 -0.58732 10.0005 1.41584C7.60884 -0.58732 4.03036 -0.4646 1.78403 1.78212C0.633032 2.93379 0 4.46401 0 6.09336C0 7.72175 0.633032 9.25292 1.78403 10.4046L8.93342 17.5582C9.22683 17.8527 9.61366 18 10.0005 18C10.3873 18 10.7731 17.8527 11.0675 17.5582L18.2169 10.4046C19.3669 9.25386 20 7.7227 20 6.09336C20 4.46401 19.3669 2.93285 18.2169 1.78212Z"
                          fill="#FEFEFE" />
                      </svg>
                    ) : (
                      <div className="ProjectCardLayoutNew__rank-value">
                        {rank}
                      </div>
                    )}

                    <svg width="12" height="32" viewBox="0 0 12 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M0.0516688 0C0.445203 0.937159 1.03704 1.78599 1.78986 2.49298C2.54267 3.19997 3.44014 3.74977 4.42567 4.10771C4.41885 2.15851 2.20114 0.0523276 0.0516688 0ZM4.49391 28.597C5.98148 26.8179 7.97401 25.7648 9.4684 26.432C8.24013 28.7802 5.74265 29.6174 4.49391 28.597ZM6.8208 25.1434C7.86483 23.1811 9.59123 21.7814 11.1948 22.1476C10.4783 24.6463 8.24695 25.9087 6.8208 25.1434ZM8.33566 21.3889C8.9498 19.3089 10.3828 17.6082 12 17.6736C11.8021 20.2377 9.89147 21.8664 8.33566 21.3889ZM9.01804 17.4643C9.19545 15.3189 10.2327 13.3828 11.8635 13.1408C12.2115 15.6983 10.6353 17.6606 9.04533 17.4643H9.01804ZM8.91568 13.4874C8.6632 11.3485 9.28416 9.24236 10.8263 8.69946C11.6998 11.1589 10.567 13.3566 8.94297 13.4874H8.91568ZM8.01495 9.56286C7.33258 7.50247 7.4827 5.29163 8.88156 4.46093C10.2668 6.77642 9.60487 9.15732 8.04224 9.58903L8.01495 9.56286ZM6.32949 5.88685C5.2104 3.97035 4.84874 1.73335 6.04289 0.654095C7.90577 2.69487 7.77612 5.18043 6.32949 5.88685ZM5.3537 6.42321C3.89342 7.15579 2.0169 6.47554 0.747688 5.11502C1.92819 4.05539 4.21414 4.52633 5.3537 6.43629V6.42321ZM6.97092 9.92916C5.38782 10.3739 3.69553 9.37318 2.77433 7.84259C4.15955 7.01189 6.26808 7.84914 6.97092 9.92916ZM7.82388 13.6182C6.18619 13.7556 4.73274 12.467 4.15272 10.8253C5.68124 10.2693 7.55776 11.4859 7.82388 13.6182ZM7.90577 17.3793C6.2749 17.2092 5.10122 15.6721 4.86921 13.9649C6.49326 13.736 8.08319 15.2404 7.90577 17.3793ZM7.2234 21.1011C5.65394 20.6236 4.81462 18.8903 4.93745 17.1765C6.59561 17.2289 7.84436 19.0211 7.2234 21.1011ZM5.79042 24.6528C4.35061 23.8875 3.90025 22.0364 4.36426 20.3358C5.98148 20.6956 6.84809 22.7036 5.81771 24.6528H5.79042ZM3.6 27.9233C2.35126 26.8964 2.33079 24.9341 3.14281 23.3446C4.65768 24.0184 5.08757 26.1638 3.6273 27.9233H3.6ZM0.188143 31.4489C4.38205 28.4427 7.2146 24.0102 8.1031 19.0632C8.99159 14.1161 7.86846 9.03084 4.96475 4.85338C7.9828 9.04293 9.2081 14.1831 8.38787 19.2133C7.56764 24.2436 4.76444 28.7802 0.556624 31.8871C0.365559 32.031 0.133553 32.0441 0.038021 31.8871C-0.057511 31.7301 0.0380208 31.5601 0.20179 31.4227L0.188143 31.4489Z"
                        fill="#F2C94C" />
                    </svg>
                  </div>
                )}

                {(!!grants) && (
                  <FlexBox width="100%" className="pcw" height={32} offsetBottom={8} gx={20}>
                    {isLoading ? (
                      <>
                      </>
                    ) : grants}
                  </FlexBox>
                )}
                {!isMobile && !isLoading && !isSuperPuperMain && <FlexBox width="100%" offsetBottom={4}>
                  {isLoading || is1_9 ? null : title}
                </FlexBox>}
                {!isMobile && subtitle && !(isLoading || is1_9) && <FlexBox offsetBottom={8}>{subtitle}</FlexBox>}
                  <FlexBox align="flex-end" width="100%" justify="space-between">
                    <div
                      onClick={(event) => event.stopPropagation()}
                      onMouseEnter={() => toggleTooltip && toggleTooltip(true)}
                      style={{ maxWidth: !isSuperPuperMain ? '70%' : '100%'}}
                    >
                      {/*{isLoading ? <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} height={20} width={100}/> : author}*/}
                      {!isLoading && isSuperPuperMain && <FlexBox width="100%">
                        {isLoading || is1_9 ? null : title}
                      </FlexBox>}
                      {isSuperPuperMain || isLoading || (isTeam && !isChannel) || is1_9 || isMobile ? null : author}
                    </div>
                    <div> {isLoading ? null : likes}</div>
                  </FlexBox>
              </FlexBox>
              {point}
            </ProjectCardWrapperNew>
        </ProjectCardContainerNew>
      )}

      {location.search.includes('display=doublegrid') && (
        <ProjectCardContainerNew
          onMouseLeave={() => toggleTooltip && toggleTooltip(false)}
          tooltipOpen={tooltipOpen}
          width={width}
          height={height}
          itemType={itemType}
          sizeMult={sizeMult}
          isAgeytomesh={isAgeytomesh}
          isLoading={true}>
          <OverflowContainer>
            <CoverImage isSquare={isSquare} files={titleImages} />
          </OverflowContainer>
          <ProjectCardWrapperDoubleNew
            className={metaAlwaysVisible ? 'meta--visible' : ''}
            onClick={(e) => onClick?.(identifier ?? '', e)}
            isLoading={isLoading}
          >
            <FlexBox className="meta" width="100%" justify={isRejected ? 'space-between' : 'flex-start'} onMouseEnter={() => toggleTooltip && toggleTooltip(true)}>
              <ActionsTools 
                status={status}
                select={undefined}
                more={more}
                bookmark={bookmarkButton}
                remove={deleteButton}
                repost={repostButton}
                actionsVisible={actionsVisible}
              />
            </FlexBox>
            <FlexBox className="meta" direction="column" directionMobile="column" width="100%">
              <FlexBox width="100%" className="pcw" height={32} offsetBottom={8} gx={20}>
                {isLoading && <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} width={32} height={32} />}
                {isLoading && <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} width={32} height={32} />}
                {!isLoading ? grants : null}
              </FlexBox>
              {!isCollectionVote && <FlexBox width="100%" offsetBottom={4}>
                {isLoading ? <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} height={32} width="100%" /> : title}
              </FlexBox>}
              {subtitle && !isLoading && <FlexBox offsetBottom={8}>{subtitle}</FlexBox>}
              <FlexBox align="flex-end" width="100%" justify="space-between">
                {!isSuperPuperMain && <div
                  onClick={(event) => event.stopPropagation()}
                  onMouseEnter={() => toggleTooltip && toggleTooltip(true)}>
                  {isLoading ? <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} height={20} width={100} /> : author}
                </div>}
                <div> {isLoading ? <Skeleton style={{ backgroundColor: theme.skeleton.skeletonBgColor }} height={20} width={25} /> : likes}</div>
              </FlexBox>
            </FlexBox>
          </ProjectCardWrapperDoubleNew>
        </ProjectCardContainerNew>
      )}

      {(isMobile || is1_9) && (
        <FlexBox directionMobile="column" offsetTop={0} direction="column">
          {isLoading && <>
            <Skeleton offsetTop={8} style={{ backgroundColor: theme.skeleton.skeletonBgColor }} width={'50%'} height={'23px'} />
            <Skeleton offsetTop={2} style={{ backgroundColor: theme.skeleton.skeletonBgColor }} width={'100%'} height={'14px'} />
          </>}
          {
            <div style={{ position: 'relative' }}>
              {isMobile && (isProgramsTab || isChannel)
                ? (
                  <div style={{ position: 'relative' }}>
                    <TitleMobile isExpanded={isExpanded} onClick={toggle} isClampable={isProgramsTab || isChannel}>
                      <div>{title}</div>
                    </TitleMobile>
                  </div>
                ) : (
                  <TitleMobile>
                    <div>{title}</div>
                  </TitleMobile>
                )}
            </div>
          }
          {subtitle && <FlexBox offsetTop={4}><SubTitleMobile>{subtitle}</SubTitleMobile></FlexBox>}
          {(!isSuperPuperMain && !isTeam) && <AuthorMobile hasSubtitle={Boolean(subtitle)} isDoubleGrid={isDoubleGrid}>{author}</AuthorMobile>}
          {!isMedia && !isDoubleGrid && likes}
        </FlexBox>
      )}
    </Link>
  );
};

export default memo(ProjectCardLayoutNew)
